/**
 * Build MAS (Direct/Indirect) save payload - form_data array from packSizeFormData.
 * Shared by both MAS Direct and MAS Indirect templates.
 */
import type { FormDataArrayItem, MASSavePayloadDeps } from './masPayloadBuilder.types';

export function buildMASSavePayload(deps: MASSavePayloadDeps): FormDataArrayItem[] {
  const { latestPackSizeFormData, gsmsHndlgType, formatDateForAPI } = deps;
  const formDataArray: FormDataArrayItem[] = [];

  Object.keys(latestPackSizeFormData).forEach(tabIndexStr => {
    const tabData = latestPackSizeFormData[parseInt(tabIndexStr)];
    if (!tabData || !tabData.conceptName || !tabData.packSize) return;

    let conceptEntry = formDataArray.find(c => c.concept_name === tabData.conceptName);
    if (!conceptEntry) {
      conceptEntry = {
        concept_name: tabData.conceptName,
        pack_size: {},
      };
      formDataArray.push(conceptEntry);
    }

    const packSizeKey = tabData.packSize.toString();
    conceptEntry.pack_size[packSizeKey] = {
      gsms_mfg_ndc:
        tabData.gsms_mfg_ndc || tabData.mfg_ndc || tabData.selectedMfgNdc || null,
      gsms_ndc: tabData.gsms_ndc || null,
      start_date: formatDateForAPI(tabData.usagePeriodStartDate) || null,
      end_date: formatDateForAPI(tabData.usagePeriodEndDate) || null,
      gsms_cost_from_manufacturer: tabData.gsmsCostFromMfg
        ? parseFloat(tabData.gsmsCostFromMfg)
        : null,
      gsms_net_cost_from_manufacturer: tabData.gsmsNetCostFromMfg
        ? parseFloat(tabData.gsmsNetCostFromMfg)
        : null,
      gsms_iff_fee: tabData.gsmsIffFee ? parseFloat(tabData.gsmsIffFee) : null,
      gsms_cost: tabData.gsmsCost ? parseFloat(tabData.gsmsCost) : null,
      suggested_bid_price: tabData.suggestedBidPrice
        ? parseFloat(tabData.suggestedBidPrice)
        : null,
      wac: tabData.wac ? parseFloat(tabData.wac) : null,
      supplier_comparable_wac: (() => {
        const value = tabData.supplier_comparable_wac;
        if (value == null || value === '' || value === undefined) return null;
        if (typeof value === 'number') return isNaN(value) ? null : value;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      })(),
      gsms_profit:
        tabData.gsmsProfit !== null && tabData.gsmsProfit !== undefined
          ? typeof tabData.gsmsProfit === 'number'
            ? tabData.gsmsProfit
            : parseFloat(tabData.gsmsProfit)
          : null,
      va_annual_usage: tabData.vaAnnualUsage ? parseFloat(tabData.vaAnnualUsage) : null,
      dod_annual_usage: tabData.dodAnnualUsage ? parseFloat(tabData.dodAnnualUsage) : null,
      va_tms_percentage: tabData.vaTmsPercent ? parseFloat(tabData.vaTmsPercent) : null,
      dod_tms_percentage: tabData.dodTmsPercent ? parseFloat(tabData.dodTmsPercent) : null,
      proposed_new_ratio: tabData.proposedNewRatio || null,
      awarded_fss_ratio: tabData.awardedFssRatio || null,
      new_tc_price: tabData.newTcPrice ? parseFloat(tabData.newTcPrice) : null,
      max_price_without_iff: tabData.maxPriceWithoutIff
        ? parseFloat(tabData.maxPriceWithoutIff)
        : null,
      max_price_with_iff: tabData.maxPriceWithIff ? parseFloat(tabData.maxPriceWithIff) : null,
      ratio_ok: tabData.ratioOk || null,
      max_price_ok: tabData.maxPriceOk || null,
      total_tms_percentage: tabData.totalTmsPercent
        ? parseFloat(tabData.totalTmsPercent)
        : null,
      target_annual_usage: tabData.targetAnnualUsage
        ? parseFloat(tabData.targetAnnualUsage)
        : null,
      contract_annual_revenue: tabData.contractAnnualRevenue
        ? parseFloat(tabData.contractAnnualRevenue)
        : null,
      contract_annual_gp: tabData.contractAnnualGP
        ? parseFloat(tabData.contractAnnualGP)
        : null,
      contract_annual_cost: tabData.contractAnnualCost
        ? parseFloat(tabData.contractAnnualCost)
        : null,
      gross_profit: tabData.grossProfit ? parseFloat(tabData.grossProfit) : null,
      gp_per_unit: tabData.gpPerUnit ? parseFloat(tabData.gpPerUnit) : null,
      government_savings_analysis: tabData.govSavingsAnalysis
        ? parseFloat(tabData.govSavingsAnalysis)
        : null,
      gsms_marketing_fee: tabData.gsmsMarketingFee
        ? parseFloat(tabData.gsmsMarketingFee)
        : null,
      tmop_percentage: tabData.tmopPercent ? parseFloat(tabData.tmopPercent) : null,
      annual_tmop_savings: tabData.annualTmopSavings
        ? parseFloat(tabData.annualTmopSavings)
        : null,
      month_tmop_savings: tabData.monthlyTmopSavings
        ? parseFloat(tabData.monthlyTmopSavings)
        : null,
      gsms_pack_size: (() => {
        if (
          tabData.mfgBottleSize !== null &&
          tabData.mfgBottleSize !== undefined &&
          tabData.mfgBottleSize !== ''
        ) {
          const mfgBottleSizeValue = tabData.mfgBottleSize;
          if (typeof mfgBottleSizeValue === 'number') return Math.round(mfgBottleSizeValue);
          const parsed = parseInt(String(mfgBottleSizeValue).trim(), 10);
          return isNaN(parsed) ? null : parsed;
        }
        return null;
      })(),
      gsms_handling_type: tabData.gsmsHndlgType || gsmsHndlgType || null,
      gsms_bottle_capacity: tabData.gsmsBottleCapacity || null,
      gsms_bottle_cost: tabData.gsmsBottleCost ? parseFloat(tabData.gsmsBottleCost) : null,
      gsms_cap_cost: tabData.gsmsCapCost ? parseFloat(tabData.gsmsCapCost) : null,
      gsms_label_cost: tabData.gsmsLabelCost ? parseFloat(tabData.gsmsLabelCost) : null,
      gsms_insert_cost: tabData.gsmsInsertCost ? parseFloat(tabData.gsmsInsertCost) : null,
      gsms_box_or_carton_cost: tabData.gsmsBoxCartonCost
        ? parseFloat(tabData.gsmsBoxCartonCost)
        : null,
      gsms_dessicant_cost: tabData.gsmsDesiccantCost
        ? parseFloat(tabData.gsmsDesiccantCost)
        : null,
      gsms_outsource_repack_fee: tabData.outsourceRepackFee
        ? parseFloat(tabData.outsourceRepackFee)
        : null,
      blended_whls_fee: tabData.blendedWhlsFee ? parseFloat(tabData.blendedWhlsFee) : null,
      gsms_frt_cost: tabData.gsmsFrtCost ? parseFloat(tabData.gsmsFrtCost) : null,
      pv2_percent_cost: tabData.pv2Percent ? parseFloat(tabData.pv2Percent) : null,
      gsms_labor: tabData.gsmsLabor ? parseFloat(tabData.gsmsLabor) : null,
      mode_wac: (() => {
        const value = tabData.modeWac;
        if (value == null || value === '') return null;
        if (typeof value === 'number') return value;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      })(),
      low_wac: (() => {
        const value = tabData.lowWac;
        if (value == null || value === '') return null;
        if (typeof value === 'number') return value;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      })(),
      top_rx_price: (() => {
        const value = tabData.topRxPrice;
        if (value == null || value === '') return null;
        if (typeof value === 'number') return value;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      })(),
      top_rx_vendor: tabData.topRxVendor || null,
    };
  });

  return formDataArray;
}
